/**
 * Exterior architectural style catalog for the Building Exterior Design tool.
 * @typedef {{ id: string; label: string; shortDescription: string; image: string | null; promptLabel?: string }} ExteriorDesignStyle
 */

/** @type {ExteriorDesignStyle[]} */
export const EXTERIOR_DESIGN_STYLES = [
  {
    id: 'modern',
    label: 'Modern',
    shortDescription: 'Clean lines, simple volumes, glass, concrete, minimal detailing.',
    image: null,
  },
  {
    id: 'contemporary',
    label: 'Contemporary',
    shortDescription: 'Current luxury exterior style with mixed materials and refined forms.',
    image: null,
  },
  {
    id: 'minimalist',
    label: 'Minimalist',
    shortDescription: 'Very clean facade, restrained materials, uncluttered look.',
    image: null,
  },
  {
    id: 'luxury-villa',
    label: 'Luxury Villa',
    shortDescription: 'Premium upscale villa exterior with elegant materials and lighting.',
    image: null,
  },
  {
    id: 'modern-luxury',
    label: 'Modern Luxury',
    shortDescription: 'Sleek modern architecture with high-end finishes and strong curb appeal.',
    image: null,
  },
  {
    id: 'classic',
    label: 'Classic',
    shortDescription: 'Balanced formal facade with timeless architectural detailing.',
    image: null,
  },
  {
    id: 'neoclassical',
    label: 'Neoclassical',
    shortDescription: 'Symmetry, columns, moldings, and a grand formal appearance.',
    image: null,
  },
  {
    id: 'mediterranean',
    label: 'Mediterranean',
    shortDescription: 'Stucco walls, warm tones, arches, and tiled-roof villa character.',
    image: null,
  },
  {
    id: 'spanish',
    label: 'Spanish',
    shortDescription: 'Warm plaster, terracotta influence, arches, and traditional southern charm.',
    image: null,
  },
  {
    id: 'italian-villa',
    label: 'Italian Villa',
    shortDescription: 'Elegant villa style with stone/stucco and refined European character.',
    image: null,
  },
  {
    id: 'modern-arabic',
    label: 'Modern Arabic',
    shortDescription: 'Modern villa aesthetic with warm materials and subtle regional character.',
    image: null,
  },
  {
    id: 'islamic-mashrabiya',
    label: 'Islamic / Mashrabiya',
    shortDescription: 'Privacy-focused facade accents with geometric screens and pattern language.',
    image: null,
  },
  {
    id: 'moroccan',
    label: 'Moroccan',
    shortDescription: 'Decorative arches, texture, warm earth tones, and artisanal character.',
    image: null,
  },
  {
    id: 'scandinavian',
    label: 'Scandinavian',
    shortDescription: 'Simple forms, bright palette, natural materials, and calm design.',
    image: null,
  },
  {
    id: 'japandi',
    label: 'Japandi',
    shortDescription: 'Clean, warm, minimal exterior mixing Japanese and Scandinavian character.',
    image: null,
  },
  {
    id: 'farmhouse',
    label: 'Farmhouse',
    shortDescription: 'Cozy residential exterior with traditional warmth and simple charm.',
    image: null,
  },
  {
    id: 'modern-farmhouse',
    label: 'Modern Farmhouse',
    shortDescription: 'Farmhouse base with modern black/white contrast and cleaner lines.',
    image: null,
  },
  {
    id: 'craftsman',
    label: 'Craftsman',
    shortDescription: 'Detailed warm exterior with stone/wood character and handcrafted feel.',
    image: null,
  },
  {
    id: 'colonial',
    label: 'Colonial',
    shortDescription: 'Traditional symmetrical facade with classic heritage appeal.',
    image: null,
  },
  {
    id: 'coastal',
    label: 'Coastal',
    shortDescription: 'Bright airy exterior with relaxed beach-house styling.',
    image: null,
  },
  {
    id: 'tropical',
    label: 'Tropical',
    shortDescription: 'Lush, breezy, resort-like exterior with natural materials.',
    image: null,
  },
  {
    id: 'industrial',
    label: 'Industrial',
    shortDescription: 'Concrete, steel, darker tones, and bold urban character.',
    image: null,
  },
  {
    id: 'rustic',
    label: 'Rustic',
    shortDescription: 'Natural textures, stone, wood, and a grounded traditional feel.',
    image: null,
  },
  {
    id: 'traditional',
    label: 'Traditional',
    shortDescription: 'Familiar residential character with timeless mainstream curb appeal.',
    image: null,
  },
];

export const EXTERIOR_DESIGN_STYLE_DEFAULT = EXTERIOR_DESIGN_STYLES[0];

export const BUILDING_EXTERIOR_STYLE_LABELS = EXTERIOR_DESIGN_STYLES.map((s) => s.label);

/**
 * @param {string | null | undefined} label
 * @returns {ExteriorDesignStyle | undefined}
 */
export function findExteriorStyleByLabel(label) {
  if (typeof label !== 'string') return undefined;
  const trimmed = label.trim();
  if (!trimmed) return undefined;
  return EXTERIOR_DESIGN_STYLES.find(
    (s) => s.label === trimmed || s.promptLabel === trimmed,
  );
}

/**
 * Resolve the prompt-facing style string for a catalog entry or legacy label.
 * @param {string} label
 * @returns {string}
 */
export function resolveExteriorStylePromptLabel(label) {
  const found = findExteriorStyleByLabel(label);
  if (found) return found.promptLabel ?? found.label;
  return typeof label === 'string' ? label.trim() : '';
}
